import {
  Info,
  Percent,
  Store,
  TrendingDown,
  TrendingUp,
  ExternalLink,
} from 'lucide-react';
import { round } from '@/utils/mathUtils';
import { useState, memo, useCallback, useMemo } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { IOneItemInPrepareForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface';
import type {
  IItemData,
  IItemDataResponse,
} from '@/pages/PurchasesContainer/RequestPerHand/api/HandRequest.interface';
import { getDiscountStatusFn } from '@/pages/PurchasesContainer/RequestPerHand/utils/DiscountCheck';
import CompetitorsDialog from '@/pages/PurchasesContainer/RequestPerOffer/components/PrepareRequest/CompetitorsDialog';
import DiscountMap from '@/pages/PurchasesContainer/RequestPerOffer/components/PrepareRequest/itemsTable/columns/DiscountCol/DiscountMap';
import UnderRequestsDialog from '@/pages/PurchasesContainer/RequestPerOffer/components/PrepareRequest/UnderRequestsDialog';

interface SummarySectionProps {
  selectedItem: IItemData | null;
  qtyInput: string;
  discountInput: string;
  specialDiscountInput?: string;
  bonusInput: string;
  taxInput: string;
  itemData: IItemDataResponse | null;
}

// Helper functions moved outside component to prevent recreation
const parseIntSafe = (value: string): number => {
  const parsed = parseInt(value || '0', 10);
  return isNaN(parsed) ? 0 : parsed;
};

const parseFloatSafe = (value: string): number => {
  const parsed = parseFloat(value || '0');
  return isNaN(parsed) ? 0 : parsed;
};

const formatNumber = (value: number): string => {
  return round(value, 3).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

// Memoized stateless summary card component
const SummaryCard = memo(
  ({
    label,
    value,
    borderColor,
    textColor,
    icon,
  }: {
    label: string;
    value: React.ReactNode;
    borderColor: string;
    textColor: string;
    icon?: React.ReactNode;
  }) => (
    <div className="flex flex-col">
      <Label className="mb-1 text-center text-xs font-medium text-gray-500 dark:text-gray-400">
        {label}
      </Label>
      <div
        className={`flex h-10 items-center justify-end border-b-2 ${borderColor} px-2 text-lg font-medium ${textColor}`}
      >
        {icon}
        {value}
      </div>
    </div>
  ),
);
SummaryCard.displayName = 'SummaryCard';

// Memoized stat card component
const StatCard = memo(
  ({
    icon,
    label,
    value,
    bgColor,
    textColor,
  }: {
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
    bgColor: string;
    textColor: string;
  }) => (
    <div className="flex flex-col items-center rounded-md border border-gray-200 p-2 dark:border-gray-700">
      <div
        className={`mb-1 flex h-8 w-8 items-center justify-center rounded-full ${bgColor}`}
      >
        {icon}
      </div>
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
      <span className={`text-lg font-medium ${textColor}`}>{value}</span>
    </div>
  ),
);
StatCard.displayName = 'StatCard';

// Define the allowed discount status types
type DiscountStatusType = 'static' | 'best' | 'accept' | 'bad' | 'only';

export const SummarySection = memo(function SummarySection({
  selectedItem,
  qtyInput,
  discountInput,
  specialDiscountInput = '',
  bonusInput,
  taxInput,
  itemData,
}: SummarySectionProps) {
  // Dialog state
  const [dialogState, setDialogState] = useState({
    showCompetitors: false,
    showUnderRequest: false,
  });

  const { showCompetitors, showUnderRequest } = dialogState;

  // Memoized handlers
  const handleShowCompetitors = useCallback(() => {
    if (!selectedItem) return;
    setDialogState((prev) => ({ ...prev, showCompetitors: true }));
  }, [selectedItem]);

  const handleShowUnderRequest = useCallback(() => {
    if (!selectedItem) return;
    setDialogState((prev) => ({ ...prev, showUnderRequest: true }));
  }, [selectedItem]);

  const handleCloseCompetitors = useCallback(() => {
    setDialogState((prev) => ({ ...prev, showCompetitors: false }));
  }, []);

  const handleCloseUnderRequest = useCallback(() => {
    setDialogState((prev) => ({ ...prev, showUnderRequest: false }));
  }, []);

  // Memoized price calculations to avoid recalculating on every render
  const { qty, netPrice, netDiscount, totalTax, discountStatus } =
    useMemo(() => {
      // Early return if no selected item
      if (!selectedItem) {
        return {
          qty: 0,
          netPrice: 0,
          netDiscount: 0,
          totalTax: 0,
          discountStatus: 'static' as DiscountStatusType,
        };
      }

      // Basic values
      const qty = qtyInput === '0' || !qtyInput ? 1 : parseIntSafe(qtyInput);
      const bonus = parseIntSafe(bonusInput);
      const totalQty = qty + bonus;
      const mainPrice = selectedItem.Price - parseFloatSafe(taxInput);

      // Discounts
      const pharmacyDiscount = (100 - parseFloatSafe(discountInput)) / 100;
      const specialDiscount =
        (100 - parseFloatSafe(specialDiscountInput)) / 100;

      // Tax
      const taxValue = parseFloatSafe(taxInput);
      const totalTax = taxValue * totalQty;

      // Calculate final prices and discounts
      const totalPrice =
        mainPrice * qty * pharmacyDiscount * specialDiscount + totalTax;
      const bonusValue = bonus > 0 ? (totalPrice / totalQty) * bonus : 0;
      const netPrice = (totalPrice - bonusValue) / qty;
      const netDiscount =
        netPrice && selectedItem.Price
          ? round((1 - netPrice / selectedItem.Price) * 100, 3)
          : 0;

      // Status with correct type
      const status = getDiscountStatusFn(netDiscount, itemData?.offers);
      const discountStatus = (status || 'static') as DiscountStatusType;

      return {
        qty,
        netPrice,
        netDiscount,
        totalTax,
        discountStatus,
      };
    }, [
      selectedItem,
      qtyInput,
      bonusInput,
      taxInput,
      discountInput,
      specialDiscountInput,
      itemData?.offers,
    ]);

  // Memoize offer data extraction
  const offerData = useMemo(() => {
    return {
      countOffers: itemData?.offers?.countOffers ?? 0,
      countSuppliers: itemData?.offers?.countSuppliers ?? 0,
      minPrice: itemData?.offers?.min ?? null,
      maxPrice: itemData?.offers?.max ?? null,
      underRequest: itemData?.offers?.underRequest ?? 0,
    };
  }, [itemData?.offers]);

  // Create a compatible item object for the dialogs - memoized
  const competitorsItem = useMemo(() => {
    if (!selectedItem) return null;

    return {
      HealthyItemID: selectedItem.ID,
      ClientItemName: selectedItem.Name,
      Discount: parseFloatSafe(discountInput),
      // Add minimum required fields with default values
      itemBodyID: '',
      isLastUpdate: false,
      itemName: selectedItem.Name,
      healthyItemPrice: selectedItem.Price,
      Qty: qty,
      TotalAfterDiscount: 0,
      Price: selectedItem.Price,
      DiscountEdit: 0,
      DiscountStatus: discountStatus || 'static',
      GroupItems: [],
      CompetitorsDiscounts: [],
      WholeSaleDiscount: 0,
      WholeSaleStock: 0,
      WholeSaleSales: 0,
      WholeSaleSalesStatus: 'needStock',
      WholeSaleDiff: 0,
      RetailDiscount: 0,
      RetailStock: 0,
      RetailSales: 0,
      RetailStatus: 'needStock',
      RetailDiff: 0,
      HealthyDiscountIN: 0,
      bestDiscount: 0,
      minDiscount: 0,
      underRequests: [],
      totalUnderRequests: offerData.underRequest || 0,
    } as IOneItemInPrepareForRequest;
  }, [
    selectedItem,
    discountInput,
    qty,
    discountStatus,
    offerData.underRequest,
  ]);

  // Prevent rendering if no selected item
  if (!selectedItem) {
    return null;
  }

  return (
    <>
      <div className="border-t border-gray-200 pt-3 dark:border-gray-700">
        <div className="grid grid-cols-3 gap-3">
          <SummaryCard
            label="إجمالي الضريبة"
            value={formatNumber(totalTax)}
            borderColor="border-indigo-500 dark:border-indigo-400"
            textColor="text-indigo-700 dark:text-indigo-300"
          />

          <SummaryCard
            label="صافي الخصم"
            value={formatNumber(netDiscount)}
            borderColor="border-green-500 dark:border-green-400"
            textColor="text-green-700 dark:text-green-300"
            icon={
              selectedItem?.Price ? (
                <DiscountMap discountStatus={discountStatus} />
              ) : null
            }
          />

          <SummaryCard
            label="صافي السعر"
            value={formatNumber(netPrice)}
            borderColor="border-blue-500 dark:border-blue-400"
            textColor="text-blue-700 dark:text-blue-300"
          />
        </div>
      </div>

      <div className="mt-3">
        <div className="flex items-center justify-between border-b border-gray-200 pb-1 dark:border-gray-700">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
            معلومات العروض
          </h4>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex h-7 items-center gap-1.5 rounded-full border-amber-300 px-3 text-xs font-medium text-amber-700 hover:bg-amber-50 hover:text-amber-800 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-900/20"
              onClick={handleShowUnderRequest}
              disabled={!selectedItem}
            >
              تحت الطلب
              <Badge
                variant="outline"
                className="ml-1 rounded-full bg-amber-100 px-1.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
              >
                {offerData.underRequest}
              </Badge>
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 rounded-full"
                >
                  <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80">
                <div className="space-y-3 p-2 text-sm">
                  <h4 className="font-medium text-indigo-700 dark:text-indigo-400">
                    معلومات تفصيلية عن العروض
                  </h4>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between border-b border-gray-100 pb-1 dark:border-gray-800">
                      <span className="text-gray-600 dark:text-gray-400">
                        عدد العروض:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-300">
                        {offerData.countOffers}
                      </span>
                    </div>

                    <div className="flex items-center justify-between border-b border-gray-100 pb-1 dark:border-gray-800">
                      <span className="text-gray-600 dark:text-gray-400">
                        عدد الموردين:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-300">
                        {offerData.countSuppliers}
                      </span>
                    </div>

                    <div className="flex items-center justify-between border-b border-gray-100 pb-1 dark:border-gray-800">
                      <span className="text-gray-600 dark:text-gray-400">
                        أقل سعر:
                      </span>
                      <span className="font-medium text-green-600 dark:text-green-400">
                        {offerData.minPrice
                          ? formatNumber(offerData.minPrice)
                          : '-'}
                      </span>
                    </div>

                    <div className="flex items-center justify-between border-b border-gray-100 pb-1 dark:border-gray-800">
                      <span className="text-gray-600 dark:text-gray-400">
                        أعلى سعر:
                      </span>
                      <span className="font-medium text-red-600 dark:text-red-400">
                        {offerData.maxPrice
                          ? formatNumber(offerData.maxPrice)
                          : '-'}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 dark:text-gray-400">
                        تحت الطلب:
                      </span>
                      <span className="font-medium text-amber-600 dark:text-amber-400">
                        {offerData.underRequest}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="justify-center"
                      onClick={handleShowUnderRequest}
                      disabled={!selectedItem}
                    >
                      تحت الطلب
                      <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      className="justify-center"
                      onClick={handleShowCompetitors}
                      disabled={!selectedItem}
                    >
                      الخصومات
                      <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <div className="mt-2 grid grid-cols-4 gap-2">
          <StatCard
            icon={
              <Percent className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            }
            label="عروض"
            value={offerData.countOffers}
            bgColor="bg-amber-100 dark:bg-amber-900/30"
            textColor="text-gray-800 dark:text-gray-200"
          />

          <StatCard
            icon={
              <Store className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            }
            label="موردين"
            value={offerData.countSuppliers}
            bgColor="bg-purple-100 dark:bg-purple-900/30"
            textColor="text-gray-800 dark:text-gray-200"
          />

          <StatCard
            icon={
              <TrendingDown className="h-4 w-4 text-green-600 dark:text-green-400" />
            }
            label="أقل سعر"
            value={offerData.minPrice ? formatNumber(offerData.minPrice) : '-'}
            bgColor="bg-green-100 dark:bg-green-900/30"
            textColor="text-gray-800 dark:text-gray-200"
          />

          <StatCard
            icon={
              <TrendingUp className="h-4 w-4 text-red-600 dark:text-red-400" />
            }
            label="أعلى سعر"
            value={offerData.maxPrice ? formatNumber(offerData.maxPrice) : '-'}
            bgColor="bg-red-100 dark:bg-red-900/30"
            textColor="text-gray-800 dark:text-gray-200"
          />
        </div>
      </div>

      {/* Conditionally render dialogs only when needed */}
      {competitorsItem && showCompetitors && (
        <CompetitorsDialog
          isOpen={showCompetitors}
          closeFn={handleCloseCompetitors}
          item={competitorsItem}
        />
      )}

      {competitorsItem && showUnderRequest && (
        <UnderRequestsDialog
          isOpen={showUnderRequest}
          closeFn={handleCloseUnderRequest}
          item={competitorsItem}
        />
      )}
    </>
  );
});

SummarySection.displayName = 'SummarySection';
